import React, { useState, useEffect } from 'react';
import { 
  Calculator, Euro, Building2, Users, Briefcase, Info, 
  ShieldCheck, Wallet, Activity, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import Footer from '../components/Footer';
import FoxLogo from '../components/FoxLogo';
import HeaderActions from '../components/HeaderActions'; 

const SalaryCalculatorPage = () => {
  const { t } = useLanguage();

  // --- STATE ---
  const [brutto, setBrutto] = useState(4500); 
  const [period, setPeriod] = useState('monthly'); 
  const [taxClass, setTaxClass] = useState(1);
  const [hasChildren, setHasChildren] = useState(false);
  const [churchTax, setChurchTax] = useState(false); 
  const [result, setResult] = useState(null);

  // --- HESAPLAMA MOTORU (2024 BMF Kurallarına Yakın Simülasyon) ---
  const calculateSalary = () => {
    let annualBrutto = period === 'monthly' ? parseFloat(brutto) * 12 : parseFloat(brutto);
    if (isNaN(annualBrutto)) annualBrutto = 0;

    // 2024 Ortalama Sigorta Oranları
    const healthInsuranceRate = 0.081; // İşçi payı (~%8.1)
    const pensionInsuranceRate = 0.093; // Emeklilik (~%9.3)
    const careInsuranceRate = hasChildren ? 0.017 : 0.023; // Bakım (Çocuklu/Çocuksuz)
    const unemploymentInsuranceRate = 0.013; // İşsizlik (~%1.3)

    const socialSecurity = annualBrutto * (healthInsuranceRate + pensionInsuranceRate + careInsuranceRate + unemploymentInsuranceRate);

    // Basit Vergi Matrahı Hesabı (Freibetrag düşülmüş hali)
    let taxableIncome = annualBrutto - socialSecurity - 11604; // Grundfreibetrag 2024
    if (taxableIncome < 0) taxableIncome = 0;

    // Vergi Sınıfı Çarpanı
    let taxClassFactor = { 1: 1.0, 2: 0.95, 3: 0.7, 4: 1.0, 5: 1.6, 6: 1.8 }[taxClass] || 1.0;
    
    // Artan Oranlı Vergi Hesabı (Basitleştirilmiş)
    let baseTax = 0;
    if (taxableIncome > 66760) {
        baseTax = taxableIncome * 0.42; // Spitzensteuersatz
    } else {
        baseTax = taxableIncome * 0.30; // Ortalama
    }

    let incomeTax = baseTax * taxClassFactor;
    
    // Soli (Yüksek gelirli değilse 0)
    let soli = incomeTax > 18130 ? incomeTax * 0.055 : 0;
    
    // Kilise Vergisi (Varsa %9)
    let kirchensteuer = churchTax ? incomeTax * 0.09 : 0;

    const totalDeductions = incomeTax + soli + kirchensteuer + socialSecurity;
    const annualNet = annualBrutto - totalDeductions;
    
    setResult({
        brutto: { month: annualBrutto / 12, year: annualBrutto },
        net: { month: annualNet / 12, year: annualNet },
        taxes: { income: incomeTax / 12, soli: soli / 12, church: kirchensteuer / 12 },
        social: {
            pension: (annualBrutto * pensionInsuranceRate) / 12,
            health: (annualBrutto * healthInsuranceRate) / 12,
            care: (annualBrutto * careInsuranceRate) / 12,
            unemployment: (annualBrutto * unemploymentInsuranceRate) / 12
        }
    });
  };

  useEffect(() => {
    calculateSalary();
  }, [brutto, period, taxClass, hasChildren, churchTax]);

  return (
    <div className="min-h-screen flex flex-col bg-[#0B1120] font-sans pb-24 relative overflow-x-hidden text-slate-200">
      
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-orange-900/10 rounded-full blur-[100px]"></div>
      </div>

      {/* HEADER */}
      <header className="bg-[#0B1120]/80 backdrop-blur-2xl border-b border-white/5 px-6 py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.scrollTo({top:0, behavior:'smooth'})}>
            <div className="bg-gradient-to-br from-orange-600 to-orange-500 p-2.5 rounded-2xl shadow-xl shadow-orange-600/20">
              <FoxLogo size={24} className="text-white" />
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl font-black tracking-tighter uppercase italic text-white leading-none">Salary<span className="text-orange-500">Pilot</span></h1>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.3em] mt-0.5">{t('tax_calc_title') || 'AI Financial Simulation'}</p>
            </div>
          </div>
          <HeaderActions />
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* LEFT: CONFIGURATION TERMINAL */}
            <div className="lg:col-span-5 space-y-8">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                  className="space-y-3"
                >
                    <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter uppercase leading-[0.9]">
                        {t('tax_calc_desc') || 'Optimize Your'} <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-300 italic">Wealth.</span>
                    </h2>
                    <p className="text-slate-400 font-medium text-sm md:text-base max-w-md">
                        {t('tax_calc_desc') || 'Real-time net salary analysis based on German tax laws (2024/25).'}
                    </p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                  className="glass-panel rounded-[2.5rem] shadow-2xl overflow-hidden ring-1 ring-white/5 bg-[#111827]/50"
                >
                    <div className="bg-white/[0.02] p-6 border-b border-white/5 flex items-center justify-between">
                        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                            <Calculator size={16} className="text-orange-600"/> {t('firm_data') || 'Parameters'}
                        </h3>
                        <div className="flex bg-white/5 rounded-lg p-1 border border-white/10">
                            {['monthly', 'yearly'].map(p => (
                                <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-md text-[9px] font-black uppercase transition-all ${period === p ? 'bg-[#1e293b] text-orange-500 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}>
                                    {p === 'monthly' ? t('monthly') || 'MONTHLY' : t('yearly') || 'YEARLY'}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="p-8 space-y-8">
                        {/* BRUTTO INPUT */}
                        <div className="space-y-4">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('gross_income') || 'Gross Income'}</Label>
                            <div className="relative group">
                                <Euro className="absolute left-5 top-1/2 -translate-y-1/2 text-orange-600 group-focus-within:text-orange-500 transition-colors" size={24} />
                                <Input 
                                    type="number" 
                                    value={brutto} 
                                    onChange={(e) => setBrutto(e.target.value)} 
                                    className="h-20 pl-14 text-3xl font-black bg-[#0B1120] border border-white/10 rounded-3xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all placeholder:text-slate-700 text-white shadow-inner" 
                                />
                            </div>
                        </div>

                        {/* TAX CLASS SELECTOR */}
                        <div className="space-y-4">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                {t('tax_class') || 'Tax Class'} <Info size={12} className="text-slate-600 cursor-help" />
                            </Label>
                            <div className="grid grid-cols-6 gap-2">
                                {[1, 2, 3, 4, 5, 6].map((cls) => (
                                    <button
                                        key={cls}
                                        onClick={() => setTaxClass(cls)}
                                        className={`h-12 rounded-xl font-black text-sm transition-all border-2 ${taxClass === cls ? 'border-orange-600 bg-orange-600 text-white shadow-lg shadow-orange-900/30 scale-105' : 'border-white/5 text-slate-500 hover:border-orange-500/30 hover:bg-white/5'}`}
                                    >
                                        {cls}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[9px] text-slate-600 leading-relaxed">
                                {taxClass === 1 && (t('tax_class_desc') || "For single employees without children.")}
                                {taxClass === 3 && (t('tax_class_desc') || "For married employees with higher income.")}
                                {taxClass === 5 && (t('tax_class_desc') || "For married employees with lower income.")}
                            </p>
                        </div>

                        {/* TOGGLE OPTIONS */}
                        <div className="grid grid-cols-2 gap-4">
                            <div onClick={() => setHasChildren(!hasChildren)} className={`p-5 rounded-[1.5rem] border-2 cursor-pointer transition-all flex flex-col items-center gap-3 text-center group ${hasChildren ? 'border-emerald-500 bg-emerald-500/5 text-emerald-500' : 'border-white/5 text-slate-400 hover:border-emerald-500/30'}`}>
                                <Users size={24} className={`transition-transform group-hover:scale-110 ${hasChildren ? 'text-emerald-500' : ''}`} />
                                <span className="text-[10px] font-black uppercase tracking-widest">{t('proc_child_benefits') || 'Children'}</span>
                            </div>
                            <div onClick={() => setChurchTax(!churchTax)} className={`p-5 rounded-[1.5rem] border-2 cursor-pointer transition-all flex flex-col items-center gap-3 text-center group ${churchTax ? 'border-purple-500 bg-purple-500/5 text-purple-500' : 'border-white/5 text-slate-400 hover:border-purple-500/30'}`}>
                                <Building2 size={24} className={`transition-transform group-hover:scale-110 ${churchTax ? 'text-purple-500' : ''}`} />
                                <span className="text-[10px] font-black uppercase tracking-widest">{t('category_tax') || 'Church Tax'}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* RIGHT: ANALYTICS DASHBOARD */}
            <div className="lg:col-span-7 space-y-8">
                <AnimatePresence mode="wait">
                    {result && (
                        <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.5, type: "spring" }} className="space-y-8">
                            
                            {/* MASTER NET CARD */}
                            <div className="bg-[#111827] rounded-[3rem] p-10 md:p-12 text-white shadow-2xl relative overflow-hidden group border border-white/10 ring-1 ring-white/5">
                                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none group-hover:bg-blue-600/20 transition-colors duration-700"></div>
                                
                                <div className="relative z-10">
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-md border border-white/10 shadow-lg">
                                            <Wallet size={24} className="text-orange-400" />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 block mb-1">{t('monthly_net') || 'Estimated Net Pay'}</span>
                                            <span className="text-xs text-slate-500 font-bold">{t('monthly') || 'Monthly'}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-baseline gap-2">
                                        <h3 className="text-7xl md:text-9xl font-black tracking-tighter leading-none text-transparent bg-clip-text bg-gradient-to-br from-white to-slate-400">
                                            {result.net.month.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h3>
                                        <span className="text-3xl md:text-4xl font-black text-orange-500 mb-2 md:mb-4">€</span>
                                    </div>
                                    
                                    <div className="mt-12 grid grid-cols-2 gap-8 pt-8 border-t border-white/10">
                                        <div>
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 flex items-center gap-2"><Briefcase size={12}/> {t('yearly_net') || 'Annual Net'}</p>
                                            <p className="text-2xl md:text-3xl font-bold text-white tracking-tight">{result.net.year.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2 flex items-center justify-end gap-2">{t('category_tax') || 'Tax'} Rate <Activity size={12}/></p>
                                            <p className="text-2xl md:text-3xl font-bold text-orange-400 tracking-tight">%{Math.round((1 - (result.net.month / result.brutto.month)) * 100)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* DEDUCTION BREAKDOWN */}
                            <div className="grid md:grid-cols-2 gap-6 font-sans">
                                {/* TAXES */}
                                <div className="glass-panel border-white/10 rounded-[2.5rem] p-8 ring-1 ring-white/5 hover:border-rose-500/30 transition-colors group bg-[#111827]/50">
                                    <div className="flex items-center gap-3 mb-6 text-rose-500">
                                        <ShieldCheck size={24} className="group-hover:scale-110 transition-transform"/>
                                        <h4 className="text-xs font-black uppercase tracking-widest">{t('category_tax') || 'Taxes'}</h4>
                                    </div>
                                    <div className="space-y-4">
                                        {[
                                            { label: t('category_tax') || 'Income Tax', val: result.taxes.income },
                                            { label: 'Soli', val: result.taxes.soli },
                                            { label: 'Church Tax', val: result.taxes.church }
                                        ].map((item, i) => (
                                            <div key={i} className="flex justify-between items-center pb-4 border-b border-white/5 last:border-0 last:pb-0">
                                                <span className="text-sm font-bold text-slate-500">{item.label}</span>
                                                <span className="font-black text-white text-base">-{item.val.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* SOCIAL */}
                                <div className="glass-panel border-white/10 rounded-[2.5rem] p-8 ring-1 ring-white/5 hover:border-blue-500/30 transition-colors group bg-[#111827]/50">
                                    <div className="flex items-center gap-3 mb-6 text-blue-500">
                                        <Briefcase size={24} className="group-hover:scale-110 transition-transform"/>
                                        <h4 className="text-xs font-black uppercase tracking-widest">{t('category_insurance') || 'Social Security'}</h4>
                                    </div>
                                    <div className="space-y-4">
                                        {[
                                            { label: 'Pension', val: result.social.pension },
                                            { label: 'Health Ins.', val: result.social.health },
                                            { label: 'Care/Unemp.', val: result.social.care + result.social.unemployment }
                                        ].map((item, i) => (
                                            <div key={i} className="flex justify-between items-center pb-4 border-b border-white/5 last:border-0 last:pb-0">
                                                <span className="text-sm font-bold text-slate-500">{item.label}</span>
                                                <span className="font-black text-white text-base">-{item.val.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
      </main>
      
      <div className="relative z-10 mt-10">
         <Footer />
      </div>
    </div>
  );
};

export default SalaryCalculatorPage;